// @flow
import * as React from 'react';
import { reisy, use } from 'reisy';

import type { Publishable } from '../api/types.js';
import Slide from './slide.js';
import * as Icons from '../components/icons.js';
import SearchButton from '../components/searchbutton.js';

export const styles = reisy`
@namespace Deck;

Transition: 500ms;

OuterWrapper {
	position: relative;
}

Wrapper {
	width: 100%;
	position: relative;
	height: 10em;

	$Media.small {
		padding-top: 50%;
		height: auto;
	}
}

Fading {
	@extends Helpers.AbsFull;
	z-index: 0;
	opacity: 1;
	transition: opacity $Transition;
}
Fading-next {
	opacity: 0;
	visibility: hidden;
}

Indicators {
	position: absolute;
	z-index: 2;
	width: auto;
	bottom: 8px;
	display: flex;
	opacity: 0.75;
}
Indicators-center {
	left: 50%;
	margin-right: -50%;
	transform: translateX(-50%);
}
Indicators-right {
	right: 0;
}
Indicators-none {
	display: none;
}

Indicator {
	cursor: pointer;
	padding: 4px;

	& {
		width: 20px;
		height: 20px;
		line-height: 12px;
	}

	svg {
		width: 12px;
		height: 12px;
	}
}

IndicatorActive {

}

Arrow {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	z-index: 2;
	cursor: pointer;
	width: 32px;
	height: 32px;
	padding: 4px;
	margin: 0 4px;
	transition: all $Consts.Transition;
	border-radius: 100%;
	box-sizing: content-box !important;

	$Media.medium {
		width: 32px;
		height: 32px;
		padding: 8px;
		margin: 0 8px;
		&:hover {
			background-color: rgba(0, 0, 0, 0.25);
		}
	}
}
ArrowLeft {
	@extends Arrow;
}
ArrowRight {
	@extends Arrow;
	right: 0;
};

search {
	@extends Header.search;
}
`;

const Indicator = ({
	active,
	onClick,
}: {
	active: boolean,
	onClick: () => void,
}) => (
	<a
		{...use(styles.Indicator, active && styles.IndicatorActive)}
		onClick={onClick}
	>
		{active ? <Icons.IndicatorActive /> : <Icons.Indicator />}
	</a>
);

function wrappingIndex(num: number, idx: number) {
	// If num was 0 this would cause NaN, which prompted same key error spam
	idx %= num || 1;
	if (idx < 0) {
		idx += num;
	}
	return idx;
}

type ContainerProps = {
	children: Array<React.Element<*>>,
	index: number,
};

class Fading extends React.Component {
	// $FlowFixMe: https://github.com/facebook/flow/issues/1964
	props: ContainerProps;

	current: number;
	next: number;
	nextElem: Element;

	setNextElem = elem => (this.nextElem = elem);

	constructor(props: ContainerProps) {
		super(props);
		this.current = props.index;
		this.next = props.index;
	}

	componentWillReceiveProps(props: ContainerProps) {
		this.current = this.next;
		this.next = props.index;
	}

	componentDidUpdate() {
		setTimeout(() => {
			if (!this.nextElem) {
				return;
			}
			this.nextElem.className = use(styles.Fading).className;
		}, 0);
	}

	render() {
		const { current, next } = this;
		const { children } = this.props;
		return (
			<section>
				<div key={current} {...use(styles.Fading)}>
					{children[current]}
				</div>
				{current !== next && (
					<div
						key={next}
						ref={this.setNextElem}
						{...use(styles.Fading, styles['Fading-next'])}
					>
						{children[next]}
					</div>
				)}
			</section>
		);
	}
}

type Props = {
	content: Publishable,
	position?: string, // TODO: actually use a specific position
	className?: string,
	indicators?: 'left' | 'center' | 'right' | 'none',
	transition?: 'fade' | 'slide', // TODO: hook up a slide mechanism!
	interval?: number,
	variant?: 'small' | 'full',
	button?: string,
};

class Deck extends React.Component {
	interval: number;
	state: { index: number };

	elem: HTMLElement;
	setElem = (elem: HTMLElement) => (this.elem = elem);

	timer: ?number = null;

	timerTick = () => {
		this.next();
		// XXX: because `next` starts a timer on its own
		this.clearTimer();
		this.startTimer(this.interval);
	};
	setIndex = (index: number) => {
		this.suspend();
		this.setState({ index: this.clamp(index) });
	};
	previous = () => this.setIndex(this.state.index - 1);
	next = () => this.setIndex(this.state.index + 1);

	constructor(props: Props) {
		super(props);
		// TODO: make index into a reactive property and avoid self redraws
		this.state = { index: 0 };
		this.interval = props.interval || 10000;

		this.timerTick = this.timerTick.bind(this);
		this.setIndex = this.setIndex.bind(this);
	}

	startTimer(time: number) {
		this.timer = setTimeout(this.timerTick, time);
	}
	clearTimer() {
		clearTimeout(this.timer);
		this.timer = null;
	}
	suspend() {
		this.clearTimer();
		this.startTimer(this.interval * 5);
	}

	componentDidMount() {
		if (!IS_BROWSER) {
			return;
		}
		window.addEventListener('scroll', this.handleViewport);
		this.handleViewport();
	}
	componentWillUnmount() {
		this.clearTimer();
		window.removeEventListener('scroll', this.handleViewport);
	}

	handleViewport = () => {
		const bbox = this.elem.getBoundingClientRect();
		const isInViewport =
			bbox.right >= 0 &&
			bbox.bottom >= 0 &&
			bbox.left <= window.innerWidth &&
			bbox.top <= window.innerHeight;
		if (!isInViewport) {
			return this.clearTimer();
		}
		if (!this.timer) {
			this.startTimer(this.interval);
		}
	};

	setIndex(index: number) {
		this.suspend();
		this.setState({ index: this.clamp(index) });
	}

	clamp(n: number) {
		return wrappingIndex(this.props.content.content.length, n);
	}

	render() {
		const {
			content,
			className,
			variant = 'small',
			button,
			indicators = 'center',
		} = this.props;
		const { index } = this.state;
		const items = content.content;

		// TODO: make it possible to switch between Fading and Sliding
		const Container = Fading;
		return (
			<article ref={this.setElem} {...use(styles.OuterWrapper, className)}>
				<div {...use(styles.InnerWrapper)}>
					<div {...use(styles.Wrapper)}>
						<Container index={index} setIndex={this.setIndex}>
							{items.map(item => (
								<Slide
									key={item.id}
									content={item}
									parent={content}
									variant={variant}
									button={button}
									wrapped
								/>
							))}
						</Container>
						{items[1] && [
							<div
								key="indicator"
								{...use(styles.Indicators, styles[`Indicators-${indicators}`])}
							>
								{items.map((_, i) => (
									<Indicator
										key={i}
										active={i === index}
										onClick={() => this.setIndex(i)}
									/>
								))}
							</div>,
							<a
								key="arrowLeft"
								{...use(styles.ArrowLeft)}
								onClick={this.previous}
							>
								<Icons.ArrowLeft />
							</a>,
							<a
								key="arrowRight"
								{...use(styles.ArrowRight)}
								onClick={this.next}
							>
								<Icons.ArrowRight />
							</a>,
						]}
					</div>
				</div>
				{styles.showSearch && (
					<div {...use(styles.search)}>
						<SearchButton />
					</div>
				)}
			</article>
		);
	}
}

export default Deck;



// WEBPACK FOOTER //
// ./lib/publishables/deck.js